import React, { useEffect, useState } from "react";
import { Button, TextField, Dialog } from "@mui/material";
import { Spinner } from "../Spinner/Spinner";

const DEVICE_TYPE_OPTIONS = [
  "VEHICLE_TRACKING",
  "SMART_METER",
  "ASSET_TRACKING",
  "POS_MACHINE",
  "HOME_AUTOMATION",
  "INDUSTRIAL_AUTOMATION",
  "SURVEILLANCE",
  "PERSONAL_TRACKER",
  "TRAFFIC_MANAGEMENT",
  "OTHERS",
];

const AddressDocumentTypes = [
  "Aadhar",
  "Passport",
  "Voter_Id",
  "Driving_Licence",
  "Arms License",
  "CGHS ECHS Card",
  "Border ID Card",
  "Tibetan Refugee Card",
  "Ration Card with address",
  "Income Tax Assessment Order",
  "Vehicle Registration Certificate",
  "Registered Sale/Lease Agreement",
  "Sri Lankan Refugees Identity Card",
  "Pensioners Card with address",
  "Kissan Passbook with address",
  "Freedom Fighter Card with address",
  "Permanent Residency Card issued by J&K Government",
  "Current Passbook of Post Office/any Schedule Bank",
  "Water Bill (not older than last three months)",
  "Electricity Bill (not older than last three months)",
  "Credit Card Statement (not older than last three months)",
  "Telephone Bill of Fixed line (not older than last three months)",
  "Certificate of address issued by Village Panchayat Head",
  "Address card with photo issued by Deptt. Of Posts Govt. of India",
  "Caste and Domicile Certificate with address and photo issued by State Government",
  "Photo Identity Card having address (of Central Govt./PSU or State Govt./PSU only)",
  "Certificate of address having Photo issued by MP/MLA/Group-A Gazetted Officer in letter head",
  "Certificate of address issued by Village Panchayat head or its equivalent authority (for rural areas)",
  "Certificate of address with photo from Govt. recognized educational institutions (for students only)",
  "Fresh Certificate carrying the identity and address of the person concerned from Head of the Government recognized educational institutions (for students only)",
  "Fresh Certificate carrying the identity and address of the person(s) concerned issued by the Lt. Colonel (for serving officers/jawans of armed forces and members of their families)",
  "Certificate of identity and address of the person(s) concerned issued by Officer Commanding of the rank of Lt. Colonel or above (for serving officers/jawans of the central paramilitary forces and members of their families)",
  "GST Certificate",
  "Trade license (MC)",
  "Certificate of Incorporation (COI)",
  "Partnership Deed",
  "Self-Declaration (For Govt. Org.)",
  "Form 16 (For Govt. Org.)",
  "Company PAN Card",
  "ROC",
  "TAX CERTIFICATE",
];

const identityDocumentTypes = [
  "Aadhar",
  "Passport",
  "Voter_Id",
  "PAN",
  "Driving_Licence",
  "Arms License",
  "CGHS ECHS Card",
  "Border ID cards",
  "Photo Credit Card",
  "Tibetan Refugee Card",
  "Pensioner Card having photo",
  "Freedom Fighter Card having photo",
  "Sri Lankan Refugees Identity Card",
  "Ration card (with photograph of the applicant)",
  "Smart card issued by CSD Defence/Paramilitary",
  "Current passbook of Post Office/any scheduled bank having photo",
  "Address card with photo issued by Deptt. of Posts Govt. of India",
  "Photo Identity Card (of Central Govt./PSU or State Govt./PSU only)",
  "Photo Identity Card issued by Govt. recognized educational institutions (for students only)",
  "Caste and Domicile Certificate with photo issued by State Government",
  "Certificate of address having Photo issued by MP/MLA/Group-A Gazetted Officer in letter head",
  "Certificate of photo identity issued by Village Panchayat head or its equivalent authority (for rural areas)",
  "Certificate of address with photo from Govt. recognized educational institutions (for students only)",
  "Fresh Certificate carrying the identity and address of the person concerned from Head of the Government recognized institutions (for students only)",
  "Fresh Certificate carrying the identity and address of the person concerned issued by the Lt. Colonel (for serving officers/jawans of the armed forces and members of their families)",
  "Certificate of identity and address of the person(s) concerned issued by Officer Commanding of the rank of Lt. Colonel or above (for serving officers/jawans of the Central paramilitary forces and members of their families)",
];

const KYC_PopUp = ({ open, onClose, onConfirm, kycForm, setKycForm }) => {
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false); // ✅ loading state
  const initialKycForm = {
    msisdn: "", // prefilled from selected row
    iccid: "",
    deviceImei: "",
    deviceType: "",
    deviceModel: "",
    deviceSerialNumber: "",
    deviceMake: "",
    engineNumber: "",
    chasisNumber: "",
    registeredMobileNumber: "",
    registrationNo: "",
    custodianName: "",
    custodianAddress: "",
    addressDocumentType: "",
    addressDocumentNumber: "",
    identityDocumentType: "",
    identityDocumentNumber: "",
    devicetypeother: "",
  };

  const validateForm = () => {
    // Required fields except devicetypeother
    for (const key of Object.keys(kycForm)) {
      if (!kycForm[key] && key !== "devicetypeother") return false;
    }

    // If deviceType = OTHERS → custom type required
    if (kycForm.deviceType === "OTHERS" && !kycForm.devicetypeother.trim()) {
      return false;
    }

    // IMEI → only digits, 12–16 length
    const imeiRegex = /^[0-9]{12,16}$/;
    if (!imeiRegex.test(kycForm.deviceImei)) return false;

    // Registration No → letters, numbers, dash allowed
    const regNoRegex = /^[A-Za-z0-9-]+$/;
    if (!regNoRegex.test(kycForm.registrationNo)) return false;

    // Custodian Name → letters, numbers, spaces only
    const custodianRegex = /^[A-Za-z0-9 ]+$/;
    if (!custodianRegex.test(kycForm.custodianName)) return false;

    // Custodian Address → allow alphanumeric, spaces, commas, dots, dashes
    const addressRegex = /^[A-Za-z0-9 ,.-]+$/;
    if (!addressRegex.test(kycForm.custodianAddress)) return false;

    // Device Type → no need regex, because it comes from dropdown
    // devicetypeother → allow alphanumeric + spaces
    if (kycForm.deviceType === "OTHERS") {
      const otherRegex = /^[A-Za-z0-9 ]+$/;
      if (!otherRegex.test(kycForm.devicetypeother)) return false;
    }

    return true;
  };

  useEffect(() => {
    if (open) {
      setTouched(false);
      setKycForm((prev) => {
        const updated = { ...initialKycForm, ...prev };
        // ✅ If no deviceType set, assign the first option as default
        if (!updated.deviceType) {
          updated.deviceType = DEVICE_TYPE_OPTIONS[0];
        }
        return updated;
      });
    }
  }, [open]);

  const handleConfirm = async () => {
    setTouched(true);
    if (!validateForm()) return;

    setLoading(true); // ✅ start loading
    try {
      await onConfirm(kycForm); // assume this can be async
      onClose();
    } catch (err) {
      console.error(err);
      alert("Something went wrong while saving KYC details.");
    } finally {
      setLoading(false); // ✅ stop loading
    }
  };

  const handleClose = () => {
    setKycForm(initialKycForm);
    setTouched(false);
    onClose();
  };

  const handleChange = (field, value) => {
    setKycForm((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <>
      {loading && <Spinner />}
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10000,
            backdropFilter: "blur(4px)",
            animation: "fadeIn 0.3s ease-out",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: "16px",
              boxShadow:
                "0 20px 50px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.1)",
              width: "520px",
              maxWidth: "90%",
              transform: "translateY(0)",
              animation: "slideUp 0.3s ease-out",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background:
                  "linear-gradient(135deg, var(--bg) 0%, var(--bg) 100%)",
                color: "white",
                padding: "1.5rem 2rem",
                fontWeight: "600",
                fontSize: "1.3rem",
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    "linear-gradient(45deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)",
                  pointerEvents: "none",
                }}
              />
              <div style={{ position: "relative", zIndex: 1 }}>KYC Update</div>
            </div>

            <div
              style={{
                padding: "2.5rem",
                maxHeight: "60vh", // restrict height
                overflowY: "auto",
              }}
            >
              {Object.keys(kycForm).map((key) => {
                const showError = touched && !kycForm[key];
                if (key === "devicetypeother") return null;
                let label;
                if (key === "registrationNo") {
                  label = "Registration NO. (eg Vehicle Reg NO.)";
                } else {
                  label = key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase());
                }

                // DEVICE TYPE FIELD
                if (key === "deviceType") {
                  const isOther = kycForm[key] === "OTHERS";

                  return (
                    <div key={key} style={{ marginBottom: "1.5rem" }}>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        label={label}
                        value={kycForm[key]}
                        onChange={(e) => {
                          const val = e.target.value;
                          handleChange(key, val);
                          if (val !== "OTHERS") {
                            handleChange("devicetypeother", "");
                          }
                        }}
                        SelectProps={{ native: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            padding: "10px 14px",
                            fontSize: "1rem",
                          },
                        }}
                      >
                        {DEVICE_TYPE_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </TextField>

                      {isOther && (
                        <TextField
                          fullWidth
                          variant="outlined"
                          label="Custom Device Type"
                          value={kycForm.devicetypeother}
                          onChange={(e) =>
                            handleChange("devicetypeother", e.target.value)
                          }
                          sx={{ marginTop: "0.5rem" }}
                        />
                      )}
                    </div>
                  );
                }
                if (key === "addressDocumentType") {
                  return (
                    <div key={key} style={{ marginBottom: "1.5rem" }}>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        // label="Address Document Type"
                        value={kycForm[key]}
                        onChange={(e) => handleChange(key, e.target.value)}
                        SelectProps={{ native: true }}
                        error={touched && !kycForm[key]}
                        helperText={
                          touched && !kycForm[key]
                            ? "This field is required"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            padding: "10px 14px",
                            fontSize: "1rem",
                          },
                        }}
                      >
                        <option value="" disabled>
                          Select Address Document Type
                        </option>
                        {AddressDocumentTypes.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </TextField>
                    </div>
                  );
                }

                if (key === "identityDocumentType") {
                  return (
                    <div key={key} style={{ marginBottom: "1.5rem" }}>
                      <TextField
                        select
                        fullWidth
                        variant="outlined"
                        // label="Address Document Type"
                        value={kycForm[key]}
                        onChange={(e) => handleChange(key, e.target.value)}
                        SelectProps={{ native: true }}
                        error={touched && !kycForm[key]}
                        helperText={
                          touched && !kycForm[key]
                            ? "This field is required"
                            : ""
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            padding: "10px 14px",
                            fontSize: "1rem",
                          },
                        }}
                      >
                        <option value="" disabled>
                          Select Identity Document Type
                        </option>
                        {identityDocumentTypes.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </TextField>
                    </div>
                  );
                }

                // OTHER FIELDS
                return (
                  <div key={key} style={{ marginBottom: "1.5rem" }}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      label={label}
                      value={kycForm[key]}
                      onChange={(e) => handleChange(key, e.target.value)}
                      error={
                        touched &&
                        (!kycForm[key] ||
                          (key === "deviceImei" &&
                            !/^[0-9]{12,16}$/.test(kycForm[key])) ||
                          (key === "registrationNo" &&
                            !/^[A-Za-z0-9-]+$/.test(kycForm[key])) ||
                          ((key === "deviceType" || key === "custodianName") &&
                            !/^[A-Za-z0-9 ]+$/.test(kycForm[key])))
                      }
                      helperText={
                        touched && !kycForm[key]
                          ? "This field is required"
                          : key === "deviceImei" &&
                            touched &&
                            !/^[0-9]{12,16}$/.test(kycForm[key])
                          ? "IMEI must be 12–16 digits"
                          : key === "registrationNo" &&
                            touched &&
                            !/^[A-Za-z0-9-]+$/.test(kycForm[key])
                          ? "Only letters, numbers and - allowed"
                          : (key === "deviceType" || key === "custodianName") &&
                            touched &&
                            !/^[A-Za-z0-9 ]+$/.test(kycForm[key])
                          ? "Only letters, numbers and spaces allowed"
                          : ""
                      }
                      disabled={key === "msisdn" || key === "iccid"}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          padding: "10px 14px",
                          fontSize: "1rem",
                        },
                      }}
                    />
                  </div>
                );
              })}
            </div>

            <div
              style={{
                padding: "1.5rem 2.5rem 2.5rem",
                display: "flex",
                gap: "1rem",
                justifyContent: "flex-end",
                backgroundColor: "#fafbfc",
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <Button
                variant="outlined"
                onClick={handleClose}
                sx={{
                  borderRadius: "8px",
                  padding: "0.75rem 1.5rem",
                  fontWeight: "500",
                  textTransform: "none",
                  borderColor: "#d1d5db",
                  color: "#6b7280",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    backgroundColor: "#f9fafb",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleConfirm}
                sx={{
                  borderRadius: "8px",
                  padding: "0.75rem 1.5rem",
                  fontWeight: "500",
                  textTransform: "none",
                  backgroundColor: "var(--bg)",
                  color: "white",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#3b46d4",
                    transform: "translateY(-1px)",
                    boxShadow: "0 8px 20px rgba(65,81,224,0.3)",
                  },
                }}
              >
                Save KYC
              </Button>
            </div>
          </div>

          <style jsx>{`
            @keyframes fadeIn {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            @keyframes slideUp {
              from {
                opacity: 0;
                transform: translateY(20px) scale(0.95);
              }
              to {
                opacity: 1;
                transform: translateY(0) scale(1);
              }
            }
          `}</style>
        </div>
      </Dialog>
    </>
  );
};

export default KYC_PopUp;
